import { useState } from 'react';
import type { OutreachLeadRow } from '../../../shared/types';
import { Modal } from '../../components/primitives';
import { useIsMobile } from '../../lib/useIsMobile';
import { publishedContacts } from './helpers';
import { promoteLead } from './api';

// ── Promote confirm ──────────────────────────────────────────────────────

export function PromoteModal({
  lead,
  onClose,
  onConfirm,
}: {
  lead: OutreachLeadRow;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const isMobile = useIsMobile();
  const contacts = publishedContacts(lead);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await promoteLead(lead);
      onConfirm();
    } catch {
      setError('Failed to promote lead.');
      setBusy(false);
    }
  };

  return (
    <Modal
      mobile="half"
      title="Promote to Leads"
      context={`${lead.name} · score ${lead.score}`}
      onClose={onClose}
      hint="creates one enquiry"
      foot={
        // Frame C keeps both buttons side by side in the sheet footer, so the
        // mobile footer is passed whole rather than split into header actions
        // by Modal's two-button lifting rule.
        isMobile ? (
          <div className="outreach-mobile-buttons">
            <button type="button" className="cbtn wide" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="cbtn wide pri"
              onClick={() => void submit()}
              disabled={busy}
            >
              Promote to Leads →
            </button>
          </div>
        ) : (
          <>
            <button className="btn" onClick={onClose}>
              Cancel
            </button>
            <button className="btn primary" onClick={() => void submit()} disabled={busy}>
              Promote to Leads →
            </button>
          </>
        )
      }
    >
      <p className="outreach-prose">
        This creates a normal enquiry in your Leads pipeline, exactly the shape a website enquiry
        arrives in. The outreach row stays here with its own status, and the two link to each other
        from then on.
      </p>
      <div className="outreach-preview">
        <div className="field">
          <div className="field-label">Name</div>
          <div className="field-value">{lead.name}</div>
        </div>
        <div className="field">
          <div className="field-label">Email</div>
          <div className="field-value">
            {contacts.emails[0]?.value ?? <span className="outreach-missing">none published</span>}
          </div>
        </div>
        <div className="field">
          <div className="field-label">Phone</div>
          <div className="field-value">
            {contacts.phone?.value ?? <span className="outreach-missing">none published</span>}
          </div>
        </div>
        <div className="field">
          <div className="field-label">Note</div>
          <div className="field-value dim">
            outreach scraper, score {lead.score}, {lead.run_date}
          </div>
        </div>
        {error && <p className="error-text span-2">{error}</p>}
      </div>
    </Modal>
  );
}
